import { useEffect, useState } from 'react';
import { ensureSocketConnected, getSocket } from '@/lib/socket';
import { useAuthStore } from '@/store/authStore';
import { apiClient } from '@/api/client';
import { setAccessTokenMem } from '@/api/tokenStore';

/**
 * v5.7.7 P3+P4 — Socket health indicator + auto-recovery.
 *
 * Цели:
 *   P3 — при `connect_error` (часто = expired auth token, backend
 *        реджектит handshake) попытаться refresh access token через
 *        REST `/api/auth/refresh` и переподключить socket с новым
 *        токеном. Это компенсирует случаи когда REST продолжает
 *        работать (interceptor сам refresh'ит) но socket не получает
 *        новый auth и зависает в reconnect-loop'е.
 *
 *   P4 — UI-баннер "соединение потеряно" пока socket disconnected.
 *        Visual feedback вместо silent fail (юзер видит что что-то не
 *        ок и не пытается слать сообщения вслепую).
 *
 * Mount'ить один раз в `App` (через `isAuthenticated` gate, потому что
 * нет смысла показывать на login-странице).
 */
export default function SocketStatusIndicator() {
  // 'ok' = socket connected, 'reconnecting' = transient, 'lost' = > N сек оффлайн.
  const [status, setStatus] = useState<'ok' | 'reconnecting' | 'lost'>('ok');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;
    const sock = getSocket();

    // Кулдаун между попытками refresh, чтобы не штормить /auth/refresh
    // при каждом reconnect-attempt.
    let lastRefreshAt = 0;
    const REFRESH_COOLDOWN_MS = 30_000;
    let lostBannerTimer: ReturnType<typeof setTimeout> | null = null;

    const showLostAfterDelay = () => {
      if (lostBannerTimer) clearTimeout(lostBannerTimer);
      lostBannerTimer = setTimeout(() => {
        setStatus('lost');
      }, 3_000); // не флешим banner на каждый мгновенный disconnect
    };

    const cancelLostTimer = () => {
      if (lostBannerTimer) {
        clearTimeout(lostBannerTimer);
        lostBannerTimer = null;
      }
    };

    const onConnect = () => {
      cancelLostTimer();
      setStatus('ok');
    };

    const onDisconnect = (reason: string) => {
      // Если сервер сам закрыл connection ('io server disconnect'),
      // socket.io не auto-reconnect'ит — нужно вызвать connect() вручную.
      // Часто это происходит при auth-фейле.
      setStatus('reconnecting');
      showLostAfterDelay();
      if (reason === 'io server disconnect') {
        sock.connect();
      }
    };

    const onConnectError = async (err: Error) => {
      setStatus('reconnecting');
      showLostAfterDelay();
      // Auth-related? Попытаемся refresh + reconnect с новым токеном.
      const msg = (err?.message || '').toLowerCase();
      const isAuth =
        msg.includes('auth') ||
        msg.includes('unauthorized') ||
        msg.includes('jwt') ||
        msg.includes('token');
      if (!isAuth) return;

      const now = Date.now();
      if (now - lastRefreshAt < REFRESH_COOLDOWN_MS) return;
      lastRefreshAt = now;

      try {
        const { data } = await apiClient.post<{ accessToken: string }>('/api/auth/refresh');
        const newToken = data.accessToken;
        setAccessTokenMem(newToken);
        sock.auth = { token: newToken };
        // Обновили creds — заставим socket.io переподключиться.
        if (sock.connected) sock.disconnect();
        sock.connect();
      } catch {
        // Refresh failed — axios interceptor сам redirect'нёт на /auth/login,
        // или если не успеет — баннер lost останется висеть. Юзер увидит.
      }
    };

    sock.on('connect', onConnect);
    sock.on('disconnect', onDisconnect);
    sock.on('connect_error', onConnectError);

    // Компонент мог смонтироваться уже после раннего connect_error. Не
    // показываем ложный "ok" и явно возобновляем остановленный Manager.
    if (sock.connected) {
      setStatus('ok');
    } else {
      setStatus('reconnecting');
      showLostAfterDelay();
      ensureSocketConnected();
    }

    return () => {
      sock.off('connect', onConnect);
      sock.off('disconnect', onDisconnect);
      sock.off('connect_error', onConnectError);
      cancelLostTimer();
    };
  }, [isAuthenticated]);

  if (status === 'ok') return null;

  if (status === 'lost') {
    return (
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] rounded-lg bg-red-700 text-white shadow-2xl px-4 py-2 text-sm flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-red-300 animate-pulse" />
        <span>Соединение потеряно, переподключаюсь…</span>
      </div>
    );
  }

  // 'reconnecting' — не показываем баннер сразу (transient).
  return null;
}
